import React from 'react';
import { Search } from 'lucide-react';
import Card from './Card';
import SegmentedControl from './SegmentedControl';
import ControlGroup from './ControlGroup';

// =============================================================================
// A tab's control bar (Oct 2026) -- one look on every tab.
// =============================================================================
// Always in this order, left to right:
//
//   Mode    what you are working on       Clinical | On-call, Leave | Call | Shifts
//   View    how it is laid out            Calendar | Staff, Rank | Team
//   Period  which dates                   the PeriodNav
//   Search  find something in the list    one fixed-width box
//   ...then Import / Export / Actions, pushed to the right edge.
//
// A tab uses only the groups it needs. Every toggle option has the same padding
// and minimum width, sized to its own words -- fixed widths per option made the
// console's bar wrap onto two rows.
// =============================================================================

/** The bar itself: a card holding captioned groups in one wrapping row. */
export function ControlBar({ children }) {
  return (
    <Card padding="p-4">
      <div className="flex flex-wrap items-end gap-x-4 gap-y-3">{children}</div>
    </Card>
  );
}

/** A Mode or View switch: a captioned toggle sized by how many options it has. */
export function ToggleGroup({ label, options, value, onChange }) {
  return (
    <ControlGroup label={label} className="shrink-0">
      <SegmentedControl options={options} value={value} onChange={onChange} fit />
    </ControlGroup>
  );
}

/** The search box: same width, icon and style on every tab. */
export function SearchBox({ value, onChange, placeholder }) {
  return (
    <ControlGroup label="Search" className="shrink-0">
      <div className="relative group w-[240px]">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 group-focus-within:text-[var(--primary)] transition-colors" />
        <input type="text" aria-label="Search" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
          className="w-full h-[36px] pl-9 pr-4 bg-slate-50 border border-slate-200 rounded-lg text-xs font-bold outline-none focus:ring-2 focus:ring-[var(--primary)]/20 focus:border-[var(--primary)] transition-all" />
      </div>
    </ControlGroup>
  );
}

/** Import / Export / Actions: pushed to the right edge of the bar. */
export function EndGroup({ label, children }) {
  return <ControlGroup label={label} className="ml-auto shrink-0">{children}</ControlGroup>;
}
